import { ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { QuestionStatis, StudentScore } from '@shared/model/analysis.models';
import { TaskAnalysisService } from '@app/task/task-analysis/task-analysis.service';
import { Question, QuestionType, SubModel } from '@shared/model/itembank.models';
import { ItembankService } from '@shared/service/itembank.service';
import { cloneDeep, uniqBy } from 'es-toolkit';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TaskTypeEnum } from '@shared/model/task.models';
import { CommitPattern } from '@shared/publish/publish.model';
import { AnswerReviewViewerService } from '../answer-review-viewer/answer-review-viewer.service';

@Component({
  selector: 'fl-question-statis-student',
  templateUrl: './question-statis-student.component.html',
  styleUrls: ['./question-statis-student.component.scss'],
  standalone: false,
})
export class QuestionStatisStudentComponent implements OnInit {
  // nzContentParams 动态传参组件（ng-zorro 22 对 signal input 跳过赋值且类型不接受），回退装饰器输入
  @Input() taskAnalysisSrv: TaskAnalysisService;
  // nzContentParams 动态传参组件（ng-zorro 22 对 signal input 跳过赋值且类型不接受），回退装饰器输入
  @Input() question: Question;
  // nzContentParams 动态传参组件（ng-zorro 22 对 signal input 跳过赋值且类型不接受），回退装饰器输入
  @Input() questionStatis: QuestionStatis;
  // nzContentParams 动态传参组件（ng-zorro 22 对 signal input 跳过赋值且类型不接受），回退装饰器输入
  @Input() childQuestion: Question;
  // nzContentParams 动态传参组件（ng-zorro 22 对 signal input 跳过赋值且类型不接受），回退装饰器输入
  @Input() currentQuestionNo: string = '';

  // nzContentParams 动态传参组件（ng-zorro 22 对 signal input 跳过赋值且类型不接受），回退装饰器输入
  @Input() students: StudentScore[] = [];
  // nzContentParams 动态传参组件（ng-zorro 22 对 signal input 跳过赋值且类型不接受），回退装饰器输入
  @Input() sharedStudents: StudentScore[] = [];
  // nzContentParams 动态传参组件（ng-zorro 22 对 signal input 跳过赋值且类型不接受），回退装饰器输入
  @Input() flagTags: Array<{ flag: number; label: string }> = [];

  private itembankSrv = inject(ItembankService);
  private messageSrv = inject(NzMessageService);
  private answerReviewSrv = inject(AnswerReviewViewerService);
  private cdRef = inject(ChangeDetectorRef);

  checked: boolean = false;
  indeterminate: boolean = false;
  filterFlag: boolean = false;

  searchNameVisible: boolean = false;
  searchName: string = '';
  searchNameStudents: StudentScore[] = null;

  tableData: Array<StudentScore & { flagLabel?: string; unfilled?: boolean }> = [];

  showSharedStudents: boolean = false;

  // 当前任务名称，用于下载作答图片时拼接文件名
  taskTitle: string = '';

  filterScore: { text: string; value: number }[] = [];
  filterScoreFn = (value: number[], item: StudentScore) => value.includes(item.score);

  filterAnswer: { text: string; value: string }[] = [];
  filterAnswerFn = (value: string[], item: StudentScore) => value.includes(item.answer);
  sortScoreFn = (a: StudentScore, b: StudentScore) => a.score - b.score;
  sortDurationFn = (a: StudentScore, b: StudentScore) => (a.duration ?? 0) - (b.duration ?? 0);
  filterCollectionFn = (value: boolean, item: StudentScore) => !!item.flag === value;

  currentPageData: readonly StudentScore[] = [];

  QuestionType = QuestionType;

  get hasShared() {
    return this.students?.some(i => i.reason);
  }

  // 是否是大题题干，并不显示小题
  get isStem() {
    return !this.childQuestion && this.question?.model === QuestionType.Stem;
  }

  get selectLength() {
    if (!this.tableData.length) return 0;
    return this.tableData.filter(i => i['checked']).length;
  }

  /** 作答用时列：当前列表所有学生都有 duration 时才展示。 */
  get showDurationColumn() {
    return this.tableData.length > 0 && this.tableData.some(student => student.duration);
  }

  ngOnInit() {
    this.taskTitle = this.taskAnalysisSrv?.task?.getValue()?.title ?? '';
    this.getTableData();
    this.fmtFilterScore();
    this.fmtFilterAnswer();
  }

  getTableData() {
    const selectStudentIds = this.tableData.filter(i => i.checked).map(i => i.userId);
    let students = this.students;
    if (this.showSharedStudents) {
      this.filterFlag = false;
      students = this.sharedStudents;
    }
    let tableData = cloneDeep(students);
    if (this.searchName) {
      tableData = tableData.filter(i => {
        return i.firstName.includes(this.searchName);
      });
    }
    if (this.filterFlag) {
      tableData = tableData.filter(i => i.flag);
    }
    tableData.forEach(i => (i.checked = selectStudentIds.includes(i.userId)));
    this.tableData = tableData.map(i => ({
      ...i,
      flagLabel: this.flagTags.find(tag => tag.flag === i.flag)?.label ?? '',
      unfilled: this.setUnfilled(i),
    }));
    this.checkChecked();
  }

  fmtFilterScore() {
    this.filterScore = uniqBy(
      this.tableData.map(student => {
        return {
          text: student.score + '分',
          value: student.score,
        };
      }),
      item => item.text
    );
  }

  fmtFilterAnswer() {
    this.filterAnswer = uniqBy(
      this.tableData.map(student => {
        return {
          text: student.answer,
          value: student.answer,
        };
      }),
      item => item.text
    );
  }

  onCheckAll() {
    // this.tableData.forEach(i => (i.checked = this.checked));
    const currentPageDataIds = this.currentPageData.map(i => i.id);
    this.tableData.forEach(i => {
      currentPageDataIds.includes(i.id) && (i.checked = this.checked);
    });
    this.checkChecked();
  }

  checkChecked() {
    this.checked = this.currentPageData.length && this.currentPageData.every(i => i.checked);
    this.indeterminate = this.currentPageData.some(i => i.checked) && !this.checked;
  }

  changeFilterFlag() {
    this.filterFlag = !this.filterFlag;
    this.getTableData();
  }

  resetSearchName() {
    this.searchNameVisible = false;
    this.searchName = '';
    this.searchNameStudents = null;
    this.getTableData();
  }

  onSearchName() {
    this.searchNameVisible = false;
    this.getTableData();
  }

  changeShared(index) {
    this.showSharedStudents = index === 1;
    this.getTableData();
  }

  changeCurrentPageData(listOfCurrentPageData: readonly StudentScore[]) {
    this.currentPageData = listOfCurrentPageData;
    this.checkChecked();
  }

  async createTargetClassroom() {
    // const classroomId = this.taskAnalysisSrv.isClassroomTask ? this.taskAnalysisSrv.task.getValue().classroomId : null;
    const gradeId = this.taskAnalysisSrv.task.getValue().gradeId;
    const name = this.taskAnalysisSrv.task.getValue().title + '第' + this.currentQuestionNo + '题 靶向班';
    const students = this.tableData.filter(i => i.checked);
    if (!students.length) {
      return this.messageSrv.info('请先 选择学生');
    }

    this.taskAnalysisSrv.createTargetClassroom(gradeId, name, students);
  }

  async flagExerciseDetail(answer: StudentScore) {
    // 主表和查看器可能持有同一学生对象，先算出新标记值再统一赋值，避免二次取反
    const nextFlag = answer.flag ? 0 : 1;
    await this.itembankSrv.flagExerciseDetail(answer.detailId, nextFlag);
    answer.flag = nextFlag;

    const _answer = this.students.find(i => i.userId === answer.userId);
    if (_answer) _answer.flag = nextFlag;

    this.getTableData();

    this.cdRef.detectChanges();
  }

  setUnfilled(answer: StudentScore) {
    const task = this.taskAnalysisSrv.task.getValue();
    const questionStatis = this.questionStatis;
    if (task?.enableJudgeProper) {
      if (questionStatis.subModel === SubModel.QUESTION_SUB_MODEL_BLANK_FILL) {
        return !answer.answer.length;
      }
    }
    // 如果是背单词任务，并且提交方式为拍照提交
    if (task.type === TaskTypeEnum.Vocabulary && task?.studentCommitPattern === CommitPattern.PHOTO) {
      return !answer.photos.length;
    }
    if (questionStatis.model === QuestionType.Subjective) {
      return !answer.photos.length;
    } else {
      return !answer.answer.length;
    }
  }

  /**
   * 打开作答复核查看器：左右切换顺序与进入时的作答列表展示顺序一致
   */
  onReviewAnswerImages(answer: StudentScore, sortedData: readonly StudentScore[]) {
    const task = this.taskAnalysisSrv.task.getValue();
    this.answerReviewSrv.open({
      questionStatis: this.questionStatis,
      currentQuestionNo: this.currentQuestionNo,
      taskTitle: this.taskTitle,
      answer,
      sortedData,
      // 复核跳转用：当前题目（小题优先）与任务信息
      questionId: this.childQuestion?.id ?? this.question?.id,
      taskId: task?.id,
      taskType: task?.type,
      taskClassId: task?.taskClassId,
      // 查看器里打标后刷新背后主表；两边共用同一批学生对象
      onFlagChange: () => this.onViewerStudentFlagChange(),
    });
  }

  /**
   * 查看器里打标后刷新背后主表
   */
  onViewerStudentFlagChange() {
    this.getTableData();
    this.cdRef.detectChanges();
  }
}
